import { Channel } from 'icreate/common/models/channel';
import {
    runningHourDefaultValues,
    digitalDefaultRange,
    exhaustDefaultValues,
    flowCounterDefaultValues,
    analogChannelDefaultValues,
    DefaultValues,
} from 'icreate/common/models/channel-defaults';
import { ImportedExcelData } from 'icreate/common/models/channel-excel';
import {
    isAnalog,
    isDigital,
    isExhGasDev,
    isExhGasMean,
    isFlowCounter,
    isRunningHour,
} from 'icreate/common/models/channel-type';

export function channelsNeedUpdate(
    excelImportedData: ImportedExcelData,
    translateSerivce
): {
    translateString: string;
    updatedImportedData;
} {
    let updatedChannels: UpdatedChannels = {
        runningHour: [],
        digitalRange: [],
        digitalExtra: [],
        flowCounterRange: [],
        flowCounterExtra: [],
        analogRange: [],
        analogExtra: [],

        exhaustGasDev: [],
        exhaustGasMean: [],
    };

    if (excelImportedData?.channels?.updated?.length > 0) {
        excelImportedData.channels.updated = excelImportedData.channels.updated.map(channel => {
            return updateChannel(channel, updatedChannels);
        });
    }

    if (excelImportedData?.channels?.created?.length > 0) {
        excelImportedData.channels.created = excelImportedData.channels.created.map(channel => {
            return updateChannel(channel, updatedChannels);
        });
    }

    let strSum = '';

    if (updatedChannels?.runningHour?.length) {
        strSum += `\n${translateSerivce.instant('channel.import.auto-modify-running-hour-channel')} \n\n`;
        updatedChannels.runningHour.forEach(channel => {
            strSum += `${channel.fcuData.fcuChannelId} : ${channel.opsData.name} \n`;
        });
    }
    if (updatedChannels?.digitalRange?.length) {
        strSum += `\n${translateSerivce.instant('channel.import.auto-modify-digital-channel')} \n\n`;
        updatedChannels.digitalRange.forEach(channel => {
            strSum += `${channel.fcuData.fcuChannelId} : ${channel.opsData.name} \n`;
        });
    }

    if (updatedChannels?.exhaustGasMean?.length) {
        strSum += `\n${translateSerivce.instant('channel.import.auto-modify-exhaust-channel')} \n\n`;
        updatedChannels.exhaustGasMean.forEach(channel => {
            strSum += `${channel.fcuData.fcuChannelId} : ${channel.opsData.name} \n`;
        });
    }

    if (updatedChannels?.flowCounterRange?.length) {
        strSum += `\n${translateSerivce.instant('channel.import.auto-modify-flow-counter-channel')} \n\n`;
        updatedChannels.flowCounterRange.forEach(channel => {
            strSum += `${channel.fcuData.fcuChannelId} : ${channel.opsData.name} \n`;
        });
    }

    if (updatedChannels?.digitalExtra?.length) {
        strSum += `\n${translateSerivce.instant('channel.import.auto-modify-default-values', { channelType: 'Digital', columns: 'Digital input filter' })} \n\n`;
        updatedChannels.digitalExtra.forEach(channel => {
            strSum += `${channel.fcuData.fcuChannelId} : ${channel.opsData.name} \n`;
        });
    }

    if (updatedChannels?.flowCounterExtra?.length) {
        strSum += `\n${translateSerivce.instant('channel.import.auto-modify-default-values', { channelType: 'Flow Counter', columns: 'Digital input filter' })} \n\n`;
        updatedChannels.flowCounterExtra.forEach(channel => {
            strSum += `${channel.fcuData.fcuChannelId} : ${channel.opsData.name} \n`;
        });
    }

    if (updatedChannels?.analogExtra?.length) {
        strSum += `\n${translateSerivce.instant('channel.import.auto-modify-default-values', { channelType: 'Analog', columns: 'Alarm recovery hysteresis' })} \n\n`;
        updatedChannels.analogExtra.forEach(channel => {
            strSum += `${channel.fcuData.fcuChannelId} : ${channel.opsData.name} \n`;
        });
    }

    if (updatedChannels?.exhaustGasDev?.length) {
        strSum += `\n${translateSerivce.instant('channel.import.auto-modify-default-values', { channelType: 'Exhaust Gas Dev', columns: 'Alarm recovery hysteresis' })} \n\n`;
        updatedChannels.exhaustGasDev.forEach(channel => {
            strSum += `${channel.fcuData.fcuChannelId} : ${channel.opsData.name} \n`;
        });
    }

    return {
        translateString: strSum,
        updatedImportedData: excelImportedData,
    };
}

function updateChannel(channel: Channel, updatedChannels): Channel {
    if (isRunningHour(channel.fcuData.channelType) && hasDifferentRanges(runningHourDefaultValues, channel)) {
        channel.fcuData.measurableRange.max = runningHourDefaultValues.measurableMax;
        channel.fcuData.measurableRange.min = runningHourDefaultValues.measurableMin;
        channel.fcuData.scale = runningHourDefaultValues.scale;

        updatedChannels.runningHour.push(channel);
    } else if (isDigital(channel.fcuData.channelType)) {
        if (!isPresent(channel.fcuData.inputData.misc.digitalInputFilter)) {
            channel.fcuData.inputData.misc.digitalInputFilter = digitalDefaultRange.digitalInputFilter;
            updatedChannels.digitalExtra.push(channel);
        }
    } else if (isExhGasMean(channel.fcuData.channelType)) {
        if (hasDifferentRanges(exhaustDefaultValues, channel)) {
            channel.fcuData.measurableRange.max = exhaustDefaultValues.measurableMax;
            channel.fcuData.measurableRange.min = exhaustDefaultValues.measurableMin;
            updatedChannels.exhaustGasMean.push(channel);
        }
    } else if (isExhGasDev(channel.fcuData.channelType)) {
        if (!isPresent(channel.fcuData.inputData.misc.hysteresis)) {
            channel.fcuData.inputData.misc.hysteresis = exhaustDefaultValues.alarmRecoveryHysteresis;
            updatedChannels.exhaustGasDev.push(channel);
        }
    } else if (isFlowCounter(channel.fcuData.channelType)) {
        if (!isPresent(channel.fcuData.inputData.misc.digitalInputFilter)) {
            channel.fcuData.inputData.misc.digitalInputFilter = flowCounterDefaultValues.digitalInputFilter;
            updatedChannels.flowCounterExtra.push(channel);
        }

        if (hasDifferentRanges(flowCounterDefaultValues, channel)) {
            channel.fcuData.measurableRange.max = flowCounterDefaultValues.measurableMax;
            channel.fcuData.measurableRange.min = flowCounterDefaultValues.measurableMin;
            channel.fcuData.scale = flowCounterDefaultValues.scale;

            updatedChannels.flowCounterRange.push(channel);
        }
    } else if (isAnalog(channel.fcuData.channelType)) {
        if (!isPresent(channel.fcuData.inputData.misc.hysteresis)) {
            channel.fcuData.inputData.misc.hysteresis = analogChannelDefaultValues.alarmRecoveryHysteresis;
            updatedChannels.analogExtra.push(channel);
        }
        if (checkAnalogRange(analogChannelDefaultValues, channel)) {
            updatedChannels.analogRange.push(channel);
        }
    }

    return channel;
}

function checkAnalogRange(range: DefaultValues, channel: Channel): boolean {
    let changed = false;

    if (!isPresent(channel.fcuData.measurableRange.max)) {
        channel.fcuData.measurableRange.max = range.measurableMax;
        changed = true;
    }

    if (!isPresent(channel.fcuData.measurableRange.min)) {
        channel.fcuData.measurableRange.min = range.measurableMin;
        changed = true;
    }

    return changed;
}

function hasDifferentRanges(range: DefaultValues, channel: Channel): boolean {
    if (range === flowCounterDefaultValues && Number(channel.fcuData.scale) !== range.scale) return true;
    if (range === runningHourDefaultValues && Number(channel.fcuData.scale) !== range.scale) return true;

    const currentMax = Number(channel.fcuData.measurableRange.max || 0);
    const currentMin = Number(channel.fcuData.measurableRange.min || 0);

    if (range === runningHourDefaultValues) {
        return Math.abs(currentMax - range.measurableMax) > 0.1 || Math.abs(currentMin - range.measurableMin) > 0.1;
    }

    return currentMax !== range.measurableMax || currentMin !== range.measurableMin;
}
const isPresent = (val: any) => val !== undefined && val !== null && val !== '';

interface UpdatedChannels {
    runningHour: Channel[];

    digitalRange: Channel[];
    digitalExtra: Channel[];

    flowCounterRange: Channel[];
    flowCounterExtra: Channel[];

    analogRange: Channel[];
    analogExtra: Channel[];

    exhaustGasDev: Channel[];
    exhaustGasMean: Channel[];
}
